import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  FiX,
  FiPlus,
  FiUpload,
  FiTrash2,
  FiEdit2,
  FiChevronDown,
  FiChevronRight,
  FiInfo,
} from "react-icons/fi";
import {
  HiOutlineXMark,
  HiOutlineCheckCircle,
  HiOutlineArrowUpTray,
  HiOutlineCpuChip,
  HiOutlineSparkles,
} from "react-icons/hi2";
import safetyObservationService from "../../../services/safetyObservationService";
import plantService from "../../../services/plantService";
import masterDataService from "../../../services/masterDataService";
import userService from "../../../services/userService";
import areaService from "../../../services/areaService";
import siteLayoutService from "../../../services/siteLayoutService";
import { aiService } from "../../../services/aiService";
import CustomMultiSelect from "./CustomMultiSelect";
import { SEMANTIC_SUCCESS_MID } from '../../../theme/chartColors';
import SharedModal from "../../../components/common/SharedModal";
import "./Schedule.css"; // Reuse CSS

const ObservationModal = ({
  isOpen,
  onClose,
  onSave,
  title,
  type,
  categories = [],
  initialData = null,
  users = [],
  agencies = [],
  sboSettings = {},
  maxOwnEmp = 0,
  maxContractorEmp = 0,
  initialFiles = [],
}) => {
  const [form, setForm] = useState({
    observation: "",
    observationType: type === "Safe" ? "" : "",
    category: "",
    subCategory: "",
    severity: "",
    actionTaken: "",
    ownEmpCount: "",
    contractorEmpCount: "",
    contractorAgencyName: "",
    ownEmpName: [],
    contractorEmpName: [],
    attachments: [],
    isClosed: true,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [aiMetadata, setAiMetadata] = useState(null);
  const [aiSuggested, setAiSuggested] = useState({});
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        // AI Tracking
        if (initialData._isAiPrefilled) {
          setAiMetadata(initialData._aiMetadata);
          setAiSuggested({
            observation: !!initialData.details,
            category: !!initialData.categoryId,
            subCategory: !!initialData.subCategory,
            observationType: !!initialData.type,
            severity: !!initialData.severity,
            actionTaken: !!initialData.actionTaken,
            isClosed: true,
          });
        } else {
          setAiMetadata(null);
          setAiSuggested({});
        }

        // Fill form
        const baseForm = {
          observation: initialData.details || "",
          ownEmpCount: initialData.ownEmpCount || "",
          contractorEmpCount: initialData.contractorEmpCount || "",
          ownEmpName: initialData.companyEmpObserved
            ? initialData.companyEmpObserved.split(",").map((s) => s.trim())
            : [],
          contractorAgencyName: initialData.contractorAgencyName || initialData.ContractorAgencyName || "",
          contractorEmpName: initialData.contractorEmpObserved && initialData.contractorEmpObserved !== "undefined"
            ? initialData.contractorEmpObserved.split(",").map((s) => s.trim())
            : [],
          category: initialData.categoryId ? String(initialData.categoryId) : "",
          subCategory: initialData.subCategory ? String(initialData.subCategory) : "",
          observationType: initialData.type || "",
          attachments: initialData.attachments || [],
        };

        if (type !== "Safe") {
          baseForm.severity = initialData.severity || "";
          baseForm.actionTaken = initialData.actionTaken || "";
          baseForm.isClosed = initialData.isClosed ?? true;
        }
        setForm(baseForm);
      } else {
        setAiMetadata(null);
        setAiSuggested({});
        setForm({
          observation: "",
          ownEmpCount: "",
          contractorEmpCount: "",
          ownEmpName: [],
          contractorAgencyName: "",
          contractorEmpName: [],
          category: "",
          subCategory: "",
          observationType: "",
          severity: "",
          actionTaken: "",
          isClosed: true,
          attachments: [],
        });
      }
      setFiles(initialFiles || []);
    }
  }, [isOpen, type, initialData, initialFiles]);

  if (!isOpen) return null;

  const handleFileClick = () => fileInputRef.current.click();
  const handleFileChange = (e) => setFiles((prev) => [...prev, ...Array.from(e.target.files)]);
  const removeFile = (index) => setFiles((prev) => prev.filter((_, i) => i !== index));
  const removeExistingAttachment = (index) => setForm(p => ({ ...p, attachments: p.attachments.filter((_, i) => i !== index) }));

  const handleChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    let newValue = inputType === "checkbox" ? checked : value;
    const maxOwn = parseInt(maxOwnEmp) || 0;
    const maxContractor = parseInt(maxContractorEmp) || 0;

    if (name === "ownEmpCount" && maxOwn > 0 && parseInt(newValue) > maxOwn) {
      toast.warn(`Cannot exceed total Company Employees (${maxOwn})`);
      return;
    }
    if (name === "contractorEmpCount" && maxContractor > 0 && parseInt(newValue) > maxContractor) {
      toast.warn(`Cannot exceed total Contractor Employees (${maxContractor})`);
      return;
    }

    setForm(prev => {
      const updated = { ...prev, [name]: newValue };
      if (name === "ownEmpName") updated.ownEmpCount = value.length;
      if (name === "contractorEmpName") updated.contractorEmpCount = value.length;
      return updated;
    });
  };

  const handleCategoryChange = (e) => setForm(p => ({ ...p, category: e.target.value, subCategory: "" }));

  const selectedCategory = categories.find(c => String(c.categoryId || c.CategoryId) === String(form.category));
  const filteredSubCategories = selectedCategory?.subCategories || selectedCategory?.SubCategories || [];

  const handleSave = async () => {
    if (!form.observation?.trim()) return toast.warn("Please enter observation.");
    if (!form.category) return toast.warn("Please select a category.");
    if (!form.observationType) return toast.warn("Please select a type.");
    if (type !== "Safe" && !form.severity) return toast.warn("Please select severity.");

    try {
      setIsUploading(true);
      const uploaded = [];
      for (const file of files) {
        const res = await safetyObservationService.uploadAttachment(file);
        uploaded.push({ attachmentId: 0, fileName: res.fileName, fileType: res.fileType, fileUrl: res.fileUrl });
      }

      onSave({
        ...form,
        ownEmpName: Array.isArray(form.ownEmpName) ? form.ownEmpName.join(", ") : form.ownEmpName,
        contractorEmpName: Array.isArray(form.contractorEmpName) ? form.contractorEmpName.join(", ") : form.contractorEmpName,
        attachments: [...form.attachments, ...uploaded],
      });
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <SharedModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isUploading}>
          {isUploading ? "Saving..." : "Save Observation"}
        </button>
      }
    >
      <div className="shared-modal-body">
        {aiMetadata && (
          <div className="ai-suggestion-banner">
            <HiOutlineSparkles size={20} /> AI Vision Scan: {aiMetadata.type} ({Math.round(aiMetadata.confidence * 100)}%)
          </div>
        )}

        <div className="form-container">
          <div className="form-section-title">Observation Details</div>
          <div className="form-row two-col">
            <div className="form-col">
              <label className="required-label">Observation:</label>
              <input type="text" name="observation" value={form.observation} onChange={handleChange} />
            </div>
            <div className="form-col">
              <label className="required-label">Type:</label>
              <select name="observationType" value={form.observationType} onChange={handleChange}>
                <option value="">Select</option>
                <option value={type === "Safe" ? "Safe Act" : "Unsafe Act"}>{type === "Safe" ? "Safe Act" : "Unsafe Act"}</option>
                <option value={type === "Safe" ? "Safe Condition" : "Unsafe Condition"}>{type === "Safe" ? "Safe Condition" : "Unsafe Condition"}</option>
              </select>
            </div>
          </div>

          <div className="form-row two-col">
            <div className="form-col">
              <label className="required-label">Category:</label>
              <select name="category" value={form.category} onChange={handleCategoryChange}>
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.categoryId || cat.CategoryId} value={cat.categoryId || cat.CategoryId}>
                    {cat.categoryName || cat.CategoryName}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-col">
              <label>Sub Category:</label>
              <select name="subCategory" value={form.subCategory} onChange={handleChange} disabled={!form.category}>
                <option value="">Select Sub Category</option>
                {filteredSubCategories.map(sub => (
                  <option key={sub.subCategoryId || sub.SubCategoryId} value={sub.subCategoryId || sub.SubCategoryId}>
                    {sub.subCategoryName || sub.SubCategoryName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {type !== "Safe" && (
            <div className="form-row two-col">
              <div className="form-col">
                <label className="required-label">Severity:</label>
                <select name="severity" value={form.severity} onChange={handleChange}>
                  <option value="">Select</option>
                  <option value="Minor">Minor</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Major">Major</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </div>
          )}

          <div className="form-section-title">Personnel</div>
          <div className="form-row two-col">
            <div className="form-col">
              <label>Company Emp Count:</label>
              <input type="number" name="ownEmpCount" value={form.ownEmpCount} onChange={handleChange} />
            </div>
            {sboSettings.nameEmployees !== false && (
              <div className="form-col">
                <CustomMultiSelect
                  name="ownEmpName"
                  label="Company Employees"
                  options={users.map(u => ({ label: u.fullName || u.userName, value: u.fullName || u.userName }))}
                  selectedValues={form.ownEmpName}
                  onChange={handleChange}
                />
              </div>
            )}
          </div>

          <div className="form-section-title">Action & Attachments</div>
          {type !== "Safe" && (
            <div className="form-row">
              <div className="form-col">
                <label>Action Taken:</label>
                <textarea name="actionTaken" value={form.actionTaken} onChange={handleChange} />
              </div>
            </div>
          )}
          
          <div className="form-row">
            <div className="form-col">
              <label>Attachments:</label>
              <button type="button" onClick={handleFileClick} className="btn-upload">Upload</button>
              <input type="file" multiple ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
              <div className="file-list">
                {form.attachments.map((at, i) => (
                  <div key={i} className="file-item">{at.fileName} <FiX onClick={() => removeExistingAttachment(i)} /></div>
                ))}
                {files.map((f, i) => (
                  <div key={i} className="file-item">{f.name} (New) <FiX onClick={() => removeFile(i)} /></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SharedModal>
  );
};

const Submitsbo = ({ isOpen, onClose, roundId, scheduleId, capaId }) => {
  const [categories, setCategories] = useState([]);
  const [plants, setPlants] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [lines, setLines] = useState([]); // Changed from areas to lines
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [agencies, setAgencies] = useState([]);

  // AI Scan states
  const [isAIFeatureOpen, setIsAIFeatureOpen] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    startTime: "",
    duration: "",
    description: "",
    natureOfWork: [],
    observationTeam: [],
    plantId: "",
    area: "",
    division: "",
    subDivision: "",
    location: "",
    subLocation: "",
    noCompanyEmp: "",
    noContractorEmp: "",
    selectCompanyEmp: [],
    selectContractorEmp: [],
  });

  const [mainFiles, setMainFiles] = useState([]);
  const mainFileInputRef = useRef(null);

  const [safeObservations, setSafeObservations] = useState([]);
  const [unsafeObservations, setUnsafeObservations] = useState([]);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    type: "",
    initialFiles: [], // Added initialFiles to modalConfig
  });
  const [editingObs, setEditingObs] = useState(null);

  // State for expand/collapse of rows
  const [expandedRows, setExpandedRows] = useState({});
  const [sboSettings, setSBOSettings] = useState({});

  // Site Layout State
  const [siteLayout, setSiteLayout] = useState(null);
  const [layoutItems, setLayoutItems] = useState({}); // { 1: [], 2: [], ... }
  const [selectedLayout, setSelectedLayout] = useState({}); // { 1: itemId, 2: itemId, ... }

  const hasAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasAutoOpenedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.plantId) {
      fetchSiteLayout(formData.plantId);
    } else {
      setSiteLayout(null);
      setLayoutItems({});
      setSelectedLayout({});
    }
  }, [formData.plantId]);

  const fetchSiteLayout = async (plantId) => {
    try {
      // Fetch layout structure
      const layoutData = await siteLayoutService.getSiteLayout(plantId);
      setSiteLayout(layoutData || null);

      if (layoutData) {
        // Optimization: Only fetch Level 1 initially
        // Subsequent levels will be fetched on demand (Lazy Loading)
        if (layoutData.level1) {
          const level1Items = await siteLayoutService.getLevelItems(plantId, 1);
          setLayoutItems(prev => ({ ...prev, [1]: level1Items || [] }));
        }
      } else {
        setLayoutItems({});
      }
    } catch (error) {
      console.error("Error fetching site layout:", error);
      setLayoutItems({});
    }
  };

  const handleLayoutChange = async (level, itemId) => {
    const nextLevel = level + 1;

    // Update selected layout
    const newSelected = { ...selectedLayout, [level]: itemId };
    // Clear subsequent levels from selection
    for (let i = nextLevel; i <= 6; i++) {
      delete newSelected[i];
    }
    setSelectedLayout(newSelected);

    // Update form data with the most specific selected area name
    // Find the selected item's name from the already fetched layoutItems
    const selectedItem = (layoutItems[level] || []).find(
      (item) => String(item.id || item.itemId) === String(itemId),
    );
    if (selectedItem) {
      setFormData((prev) => ({
        ...prev,
        area: selectedItem.name,
      }));
    }

    // Lazy load next level items if needed
    if (itemId && siteLayout && siteLayout[`level${nextLevel}`]) {
      if (!layoutItems[nextLevel] || layoutItems[nextLevel].length === 0) {
        try {
          // Use formData.plantId as context for fetching
          const items = await siteLayoutService.getLevelItems(
            formData.plantId,
            nextLevel,
          );
          setLayoutItems((prev) => ({ ...prev, [nextLevel]: items || [] }));
        } catch (err) {
          console.error(`Error fetching level ${nextLevel} items:`, err);
        }
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadMasterData();
      if (roundId) {
        fetchRoundData(roundId);
      } else {
        // Reset form for new entry
        setFormData({
          date: new Date().toISOString().split("T")[0],
          startTime: "",
          duration: "",
          description: "",
          natureOfWork: [],
          observationTeam: [],
          plantId: "", // Will be auto-selected by the useEffect below if single plant
          departmentId: "",
          area: "",
          noCompanyEmp: "",
          noContractorEmp: "",
          selectCompanyEmp: [],
          selectContractorEmp: [],
          observationNumber: "",
        });
        setSafeObservations([]);
        setUnsafeObservations([]);
      }
    }
  }, [isOpen, roundId]);

  // Handle auto-selection when plants are loaded for a new entry
  useEffect(() => {
    if (isOpen && !roundId && plants?.length === 1 && !formData.plantId) {
      setFormData(prev => ({
        ...prev,
        plantId: plants[0].plantId.toString()
      }));
    }
  }, [isOpen, roundId, plants, formData.plantId]);

  // Auto-open observation modal if linked to current CAPA
  // Auto-open observation modal if linked to current CAPA
  useEffect(() => {
    // console.log("Auto-open Debug:", { isOpen, capaId, safeCount: safeObservations.length, unsafeCount: unsafeObservations.length, hasAutoOpened: hasAutoOpenedRef.current });

    if (
      isOpen &&
      capaId &&
      (safeObservations.length > 0 || unsafeObservations.length > 0) &&
      !hasAutoOpenedRef.current
    ) {
      const cIdStr = String(capaId);

      // Check Safe Observations
      const safeObs = safeObservations.find((o) => {
        return o.linkedCapaId && String(o.linkedCapaId) === cIdStr;
      });
      if (safeObs) {
        // console.log("Found Match Safe:", safeObs);
        setTimeout(() => {
          openModal("Edit Safe Observation", "Safe", safeObs);
        }, 100);
        hasAutoOpenedRef.current = true;
        return;
      }

      // Check Unsafe Observations
      const unsafeObs = unsafeObservations.find((o) => {
        return o.linkedCapaId && String(o.linkedCapaId) === cIdStr;
      });
      if (unsafeObs) {
        // console.log("Found Match Unsafe:", unsafeObs);
        setTimeout(() => {
          openModal("Edit Unsafe Observation", "Unsafe", unsafeObs);
        }, 100);
        hasAutoOpenedRef.current = true;
        return;
      }
    }
  }, [isOpen, capaId, safeObservations, unsafeObservations]);

  const loadMasterData = async () => {
    try {
      const tenantId = localStorage.getItem("tenantId") || 0;
      const promises = [];

      // 1. Users
      if (users.length === 0) {
        promises.push(
          userService
            .getAllUsers()
            .then((usrs) => setUsers(usrs || []))
            .catch((err) => console.error("Error loading users:", err)),
        );
      }

      // 2. Plants (Critical for dropdown delay)
      if (plants.length === 0) {
        promises.push(
          plantService
            .getAllPlants()
            .then((pts) => setPlants(pts || []))
            .catch((err) => console.error("Error loading plants:", err)),
        );
      }

      // 3. Categories
      if (categories.length === 0) {
        promises.push(
          safetyObservationService
            .getCategories()
            .then((cats) => setCategories(cats || []))
            .catch((err) => console.error("Error loading categories:", err)),
        );
      }

      // 4. Departments
      if (departments.length === 0) {
        promises.push(
          masterDataService
            .getDepartments(tenantId)
            .then((depts) => setDepartments(depts || []))
            .catch((err) => console.error("Error loading departments:", err)),
        );
      }

      // 5. Areas (Removed, replaced by site layout)
      // if (areas.length === 0) {
      //   promises.push(
      //     areaService
      //       .getAllAreas()
      //       .then((areas) => setAreas(areas || []))
      //       .catch((err) => console.error("Error loading areas:", err)),
      //   );
      // }

      // 6. Settings
      if (Object.keys(sboSettings).length === 0) {
        promises.push(
          safetyObservationService
            .getSBOSettings()
            .then((settings) => setSBOSettings(settings || {}))
            .catch((err) => console.error("Error loading settings:", err)),
        );
      }

      // 7. Agencies
      if (agencies.length === 0) {
        promises.push(
          masterDataService
            .getContractorAgencies(tenantId, "Active")
            .then((agencyList) => setAgencies(agencyList || []))
            .catch((err) => console.error("Error loading agencies:", err)),
        );
      }

      await Promise.all(promises);
    } catch (error) {
      console.error("Error initiating master data load:", error);
    }
  };

  const initializeLayoutFromArea = async (plantId, areaName) => {
    if (!plantId || !areaName) return;

    try {
      let foundItem = null;
      let foundLevel = 0;

      // Search all levels in parallel to find the item by name
      // This reduces N serial requests to 1 parallel request batch
      const levels = [1, 2, 3, 4, 5, 6];
      const searchPromises = levels.map((level) =>
        siteLayoutService
          .searchLevelItems(plantId, level, areaName)
          .then((items) => ({ level, items: items || [] }))
          .catch(() => ({ level, items: [] })),
      );

      const results = await Promise.all(searchPromises);

      // Find the first match in level order
      for (const res of results) {
        const match = res.items.find((i) => i.name === areaName);
        if (match) {
          foundItem = match;
          foundLevel = res.level;
          break;
        }
      }

      if (foundItem) {
        const newSelected = {};
        const newItems = {};

        newSelected[foundLevel] = foundItem.id;

        // Traverse up to find parents
        let curr = foundItem;
        let lvl = foundLevel;

        while (lvl > 1 && curr.parentId) {
          const parent = await siteLayoutService.getLevelItemById(
            curr.parentId,
          );
          if (parent) {
            newSelected[lvl - 1] = parent.id;
            curr = parent;
            lvl--;
          } else {
            break;
          }
        }

        // Fetch items for all levels in the path in PARALLEL
        const fetchPromises = [];

        // Level 1
        fetchPromises.push(
          siteLayoutService.getLevelItems(plantId, 1).then((items) => {
            newItems[1] = items || [];
          }),
        );

        // Children for other levels (fetched in parallel)
        for (let l = 2; l <= foundLevel; l++) {
          fetchPromises.push(
            siteLayoutService.getLevelItems(plantId, l).then((items) => {
              newItems[l] = items || [];
            }),
          );
        }

        // Fetch next level if available
        if (foundLevel < 6) {
          fetchPromises.push(
            siteLayoutService
              .getLevelItems(plantId, foundLevel + 1)
              .then((items) => {
                newItems[foundLevel + 1] = items || [];
              }),
          );
        }

        await Promise.all(fetchPromises);

        setLayoutItems((prev) => ({ ...prev, ...newItems }));
        setSelectedLayout(newSelected);
      }
    } catch (error) {
      console.error("Error restoring layout:", error);
    }
  };

  const fetchRoundData = async (id) => {
    try {
      setLoading(true);
      const data =
        await safetyObservationService.getSafetyObservationRoundById(id);
      if (data) {
        setFormData({
          date:
            data.observationDate || data.ObservationDate
              ? new Date(data.observationDate || data.ObservationDate)
                .toISOString()
                .split("T")[0]
              : "",
          startTime: data.observationTime || data.ObservationTime || "",
          duration: data.observationDuration || data.ObservationDuration || "",
          description: data.description || data.Description || "",
          natureOfWork:
            data.natureOfWork || data.NatureOfWork
              ? typeof (data.natureOfWork || data.NatureOfWork) === "string"
                ? (data.natureOfWork || data.NatureOfWork)
                  .split(",")
                  .map((n) => n.trim())
                : data.natureOfWork || data.NatureOfWork
              : [],
          observationTeam:
            data.observers?.map((o) =>
              String(
                o.observerUserId || o.ObserverUserId || o.userId || o.UserId,
              ),
            ) || [],
          plantId: data.plantId || data.PlantId || "",
          departmentId: data.departmentId || data.DepartmentId || "",
          area: data.area || data.Area || "",
          noCompanyEmp:
            data.companyEmployeeCount || data.CompanyEmployeeCount || "",
          noContractorEmp:
            data.contractorEmployeeCount || data.ContractorEmployeeCount || "",
          selectCompanyEmp: Array.isArray(
            data.companyEmployeeNames || data.CompanyEmployeeNames,
          )
            ? data.companyEmployeeNames || data.CompanyEmployeeNames
            : typeof (
              data.companyEmployeeNames || data.CompanyEmployeeNames
            ) === "string"
              ? (data.companyEmployeeNames || data.CompanyEmployeeNames)
                .split(",")
                .map((s) => s.trim())
              : [],
          selectContractorEmp: Array.isArray(
            data.contractorEmployeeNames || data.ContractorEmployeeNames,
          )
            ? data.contractorEmployeeNames || data.ContractorEmployeeNames
            : typeof (
              data.contractorEmployeeNames || data.ContractorEmployeeNames
            ) === "string"
              ? (data.contractorEmployeeNames || data.ContractorEmployeeNames)
                .split(",")
                .map((s) => s.trim())
              : [],
          observationNumber:
            data.observationNumber || data.ObservationNumber || "",
        });

        // Initialize layout if plant and area exist
        const pId = data.plantId || data.PlantId;
        const areaName = data.area || data.Area;
        if (pId) {
          // Fetch site layout structure first
          await fetchSiteLayout(pId);
          if (areaName && areaName !== "General Area") {
            await initializeLayoutFromArea(pId, areaName);
          }
        }

        const allItems = data.observationItems || data.items || [];

        const safe = allItems
          .filter(
            (i) =>
              (i.observationType || i.ObservationType) === "Safe Act" ||
              (i.observationType || i.ObservationType) === "Safe Condition",
          )
          .map((i, idx) => {
            const obsName = i.observerName || i.ObserverName || "";
            const observerType = i.observerType || i.ObserverType || "";
            const observationType =
              i.observationType || i.ObservationType || "Safe Act";
            let compNames = "";
            let contNames = "";

            if (obsName.startsWith("COMP:") && obsName.includes("|CONT:")) {
              const parts = obsName.split("|CONT:");
              compNames = parts[0].replace("COMP:", "");
              contNames = parts[1];
            } else {
              if (observerType.toLowerCase().includes("company"))
                compNames = obsName;
              if (observerType.toLowerCase().includes("contractor"))
                contNames = obsName;
            }

            return {
              id: i.itemId || i.ItemId || idx + 1,
              details: i.description || i.Description,
              categoryId: i.categoryId || i.CategoryId,
              category:
                categories.find(
                  (c) =>
                    String(c.categoryId || c.CategoryId) ===
                    String(i.categoryId || i.CategoryId),
                )?.categoryName ||
                categories.find(
                  (c) =>
                    String(c.categoryId || c.CategoryId) ===
                    String(i.categoryId || i.CategoryId),
                )?.CategoryName ||
                "Unknown",
              type: observationType,
              subCategory: i.subCategoryId || i.SubCategoryId || "",
              companyEmpObserved: compNames,
              contractorEmpObserved: contNames,
              ownEmpCount:
                i.companyEmployeeCount || i.CompanyEmployeeCount || 0,
              contractorEmpCount:
                i.contractorEmployeeCount || i.ContractorEmployeeCount || 0,
              contractorAgencyName:
                i.contractorAgencyName || i.ContractorAgencyName || "",
              attachments: i.attachments || i.Attachments || [],
              linkedCapaId: i.linkedCapaId || i.LinkedCapaId,
            };
          });

        const unsafe = allItems
          .filter(
            (i) =>
              ((i.observationType || i.ObservationType) === "Unsafe Act" ||
                (i.observationType || i.ObservationType) ===
                "Unsafe Condition") &&
              (i.description || i.Description),
          )
          .map((i, idx) => {
            const obsName = i.observerName || i.ObserverName || "";
            const observerType = i.observerType || i.ObserverType || "";
            const observationType =
              i.observationType || i.ObservationType || "Unsafe Act";
            let compNames = "";
            let contNames = "";

            if (obsName.startsWith("COMP:") && obsName.includes("|CONT:")) {
              const parts = obsName.split("|CONT:");
              compNames = parts[0].replace("COMP:", "");
              contNames = parts[1];
            } else {
              if (observerType.toLowerCase().includes("company"))
                compNames = obsName;
              if (observerType.toLowerCase().includes("contractor"))
                contNames = obsName;
            }

            return {
              id: i.itemId || i.ItemId || idx + 1,
              details: i.description || i.Description,
              categoryId: i.categoryId || i.CategoryId,
              category:
                categories.find(
                  (c) =>
                    String(c.categoryId || c.CategoryId) ===
                    String(i.categoryId || i.CategoryId),
                )?.categoryName ||
                categories.find(
                  (c) =>
                    String(c.categoryId || c.CategoryId) ===
                    String(i.categoryId || i.CategoryId),
                )?.CategoryName ||
                "Unknown",
              type: observationType,
              severity: i.riskLevel || i.RiskLevel,
              actionTaken: i.actionDescription || i.ActionDescription,
              isClosed: (i.itemStatus || i.ItemStatus) === "Closed",
              subCategory: i.subCategoryId || i.SubCategoryId || "",
              companyEmpObserved: compNames,
              contractorEmpObserved: contNames,
              ownEmpCount:
                i.companyEmployeeCount || i.CompanyEmployeeCount || 0,
              contractorEmpCount:
                i.contractorEmployeeCount || i.ContractorEmployeeCount || 0,
              contractorAgencyName:
                i.contractorAgencyName || i.ContractorAgencyName || "",
              attachments: i.attachments || i.Attachments || [],
              linkedCapaId: i.linkedCapaId || i.LinkedCapaId,
            };
          });

        setSafeObservations(safe);
        setUnsafeObservations(unsafe);
      }
    } catch (error) {
      console.error("Error fetching round data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMainFileClick = () => {
    mainFileInputRef.current.click();
  };

  const handleMainFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setMainFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeMainFile = (index) => {
    setMainFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "plantId") {
      setLayoutItems({});
      setSelectedLayout({});
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        departmentId: "",
        area: "",
      }));
      return;
    }

    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      return newData;
    });
  };

  // Specific handler for MultiSelect to match signature
  const handleMultiSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      let newFormData = { ...prev, [name]: value };
      // Auto-calculate counts based on name selections
      if (name === "selectCompanyEmp" && Array.isArray(value)) {
        newFormData.noCompanyEmp = value.length;
      } else if (name === "selectContractorEmp" && Array.isArray(value)) {
        newFormData.noContractorEmp = value.length;
      }
      return newFormData;
    });
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const openModal = (title, type, obs = null, initialFilesForObs = []) => {
    setEditingObs(obs);
    setModalConfig({
      isOpen: true,
      title,
      type,
      initialFiles: initialFilesForObs,
    });
  };

  const handleAiScanClick = () => {
    setIsAIFeatureOpen(true);
  };

  const handleAiScanComplete = async (result, files) => {
    if (!result || !files || files.length === 0) return;
    const file = files[0];

    try {
      const obsTypeRaw = result.observation_type || "";
      let obsType = "";

      // Exact mapping to dropdown values
      // Must check for "unsafe" first, otherwise "unsafe condition".includes("safe condition") evaluates to TRUE
      if (obsTypeRaw.toLowerCase().includes("unsafe condition"))
        obsType = "Unsafe Condition";
      else if (obsTypeRaw.toLowerCase().includes("unsafe act"))
        obsType = "Unsafe Act";
      else if (obsTypeRaw.toLowerCase().includes("safe condition"))
        obsType = "Safe Condition";
      else if (obsTypeRaw.toLowerCase().includes("safe act"))
        obsType = "Safe Act";

      // Force AI scan to Unsafe since the button is located in the Unsafe Observations section
      if (obsType === "Safe Act") obsType = "Unsafe Act";
      if (obsType === "Safe Condition") obsType = "Unsafe Condition";
      if (!obsType) obsType = "Unsafe Act";

      const isSafe = false;
      const title = "Add Unsafe Observation";
      const type = "Unsafe";

      const aiCategoryStr = (result.category || "")
        .toLowerCase()
        .replace(/— safe|— unsafe|- safe|- unsafe|— unsafe|— safe/g, "")
        .trim();

      // Narrow down categories to those appropriate for our detected type (Safe vs Unsafe)
      const potentialCategories = categories.filter((cat) => {
        const catName = (
          cat.categoryName ||
          cat.CategoryName ||
          ""
        ).toUpperCase();
        if (isSafe) {
          // For safe observations, exclude anything with "UNSAFE"
          return !catName.includes("UNSAFE");
        } else {
          // For unsafe observations, exclude anything with "SAFE"
          return !(catName.includes("— SAFE") || catName.includes("- SAFE"));
        }
      });

      let matchedCategoryId = "";
      let matchedSubCategoryId = "";

      if (aiCategoryStr) {
        const matched = potentialCategories.find((c) => {
          const comp = (c.categoryName || c.CategoryName || "")
            .toLowerCase()
            .replace(/— safe|— unsafe|- safe|- unsafe|— unsafe|— safe/g, "")
            .trim();
          return comp.includes(aiCategoryStr) || aiCategoryStr.includes(comp);
        });

        if (matched) {
          matchedCategoryId = String(matched.categoryId || matched.CategoryId);

          if (
            result.subcategory &&
            (matched.subCategories || matched.SubCategories)
          ) {
            const aiSubCategoryStr = result.subcategory.toLowerCase().trim();
            const subCats = matched.subCategories || matched.SubCategories;
            const matchedSub = subCats.find((sc) => {
              const scName = (sc.subCategoryName || sc.SubCategoryName || "")
                .toLowerCase()
                .trim();
              return (
                scName === aiSubCategoryStr ||
                scName.includes(aiSubCategoryStr) ||
                aiSubCategoryStr.includes(scName)
              );
            });
            if (matchedSub) {
              matchedSubCategoryId = String(
                matchedSub.subCategoryId || matchedSub.SubCategoryId,
              );
            }
          }
        }
      }

      const obsData = {
        details: result.description || "",
        type: obsType || (isSafe ? "Safe Act" : "Unsafe Act"),
        categoryId: matchedCategoryId,
        subCategory: matchedSubCategoryId,
        severity: (
          result.risk_level ||
          result.severity ||
          (isSafe ? "" : "Moderate")
        ).replace(/^\w/, (c) => c.toUpperCase()), // Ensure Title Case (e.g. "moderate" -> "Moderate")
        actionTaken: result.suggested_action || result.action_taken || "",
        isClosed: result.requires_action ? false : true,
        attachments: [],
        _isAiPrefilled: true,
        _aiMetadata: {
          confidence: result.ai_confidence || 0,
          category: result.category || "General",
          subcategory: result.subcategory || "Other",
          type: result.observation_type || obsType,
        },
      };

      // Match the exact Title the ADD button uses for consistency
      const finalTitle = "Unsafe Observation";
      openModal(finalTitle, type, obsData, files);
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while processing scanning results.");
    }
  };

  const handleObservationSave = async (newObs) => {
    const selectedCat = categories.find((c) => c.categoryId == newObs.category);
    let catName =
      selectedCat?.categoryName || selectedCat?.CategoryName || "Unknown";
    catName = catName.replace(/— SAFE|- SAFE|— UNSAFE|- UNSAFE/g, "").trim();

    let updatedSafeObservations = null;
    let updatedUnsafeObservations = null;

    if (modalConfig.type === "Safe") {
      if (editingObs) {
        updatedSafeObservations = safeObservations.map((o) =>
          o.id === editingObs.id
            ? {
              ...o,
              details: newObs.observation,
              categoryId: parseInt(newObs.category),
              category: catName,
              type: newObs.observationType,
              subCategory: newObs.subCategory,
              companyEmpObserved: Array.isArray(newObs.ownEmpName)
                ? newObs.ownEmpName.join(", ")
                : newObs.ownEmpName,
              contractorEmpObserved: Array.isArray(newObs.contractorEmpName)
                ? newObs.contractorEmpName.join(", ")
                : newObs.contractorEmpName,
              ownEmpCount: newObs.ownEmpCount,
              contractorEmpCount: newObs.contractorEmpCount,
              contractorAgencyName: newObs.contractorAgencyName,
              attachments: newObs.attachments,
            }
            : o,
        );
      } else {
        const newId =
          safeObservations.length > 0
            ? Math.max(...safeObservations.map((o) => o.id)) + 1
            : 1;
        updatedSafeObservations = [
          ...safeObservations,
          {
            id: newId,
            details: newObs.observation,
            categoryId: parseInt(newObs.category),
            category: catName,
            type: newObs.observationType,
            subCategory: newObs.subCategory,
            companyEmpObserved: Array.isArray(newObs.ownEmpName)
              ? newObs.ownEmpName.join(", ")
              : newObs.ownEmpName,
            contractorEmpObserved: Array.isArray(newObs.contractorEmpName)
              ? newObs.contractorEmpName.join(", ")
              : newObs.contractorEmpName,
            ownEmpCount: newObs.ownEmpCount,
            contractorEmpCount: newObs.contractorEmpCount,
            contractorAgencyName: newObs.contractorAgencyName,
            attachments: newObs.attachments,
          },
        ];
      }
      setSafeObservations(updatedSafeObservations);
    } else {
      if (editingObs) {
        updatedUnsafeObservations = unsafeObservations.map((o) =>
          o.id === editingObs.id
            ? {
              ...o,
              details: newObs.observation,
              categoryId: parseInt(newObs.category),
              category: catName,
              type: newObs.observationType,
              severity: newObs.severity,
              subCategory: newObs.subCategory,
              actionTaken: newObs.actionTaken,
              isClosed: newObs.isClosed,
              companyEmpObserved: Array.isArray(newObs.ownEmpName)
                ? newObs.ownEmpName.join(", ")
                : newObs.ownEmpName,
              contractorEmpObserved: Array.isArray(newObs.contractorEmpName)
                ? newObs.contractorEmpName.join(", ")
                : newObs.contractorEmpName,
              ownEmpCount: newObs.ownEmpCount,
              contractorEmpCount: newObs.contractorEmpCount,
              contractorAgencyName: newObs.contractorAgencyName,
              attachments: newObs.attachments,
            }
            : o,
        );
      } else {
        const newId =
          unsafeObservations.length > 0
            ? Math.max(...unsafeObservations.map((o) => o.id)) + 1
            : 1;
        updatedUnsafeObservations = [
          ...unsafeObservations,
          {
            id: newId,
            details: newObs.observation,
            categoryId: parseInt(newObs.category),
            category: catName,
            type: newObs.observationType,
            severity: newObs.severity,
            subCategory: newObs.subCategory,
            actionTaken: newObs.actionTaken,
            isClosed: newObs.isClosed,
            companyEmpObserved: Array.isArray(newObs.ownEmpName)
              ? newObs.ownEmpName.join(", ")
              : newObs.ownEmpName,
            contractorEmpObserved: Array.isArray(newObs.contractorEmpName)
              ? newObs.contractorEmpName.join(", ")
              : newObs.contractorEmpName,
            ownEmpCount: newObs.ownEmpCount,
            contractorEmpCount: newObs.contractorEmpCount,
            contractorAgencyName: newObs.contractorAgencyName,
            attachments: newObs.attachments,
          },
        ];
      }
      setUnsafeObservations(updatedUnsafeObservations);
    }

    setEditingObs(null);
    setModalConfig({ isOpen: false, title: "", type: "", initialFiles: [] }); // Reset initialFiles
    // If linked from CAPA, trigger immediate save
    if (capaId) {
      await handleSave(
        false,
        updatedSafeObservations,
        updatedUnsafeObservations,
      );
    }
  };

  const deleteObservation = (id, type) => {
    if (type === "Safe") {
      setSafeObservations((prev) => prev.filter((o) => o.id !== id));
    } else {
      setUnsafeObservations((prev) => prev.filter((o) => o.id !== id));
    }
  };

  const natureOptions = [
    { value: "Electrical", label: "Electrical" },
    { value: "Mechanical", label: "Mechanical" },
    { value: "Civil", label: "Civil" },
    { value: "Instrumentation", label: "Instrumentation" },
    { value: "Height Work", label: "Height Work" },
    { value: "Confined Space", label: "Confined Space" },
    { value: "Lifting", label: "Lifting" },
    { value: "General", label: "General" },
  ];

  const handleSaveDraft = () => {
    handleSave(true);
  };

  const handleSave = async (
    isDraft = false,
    overrideSafeObs = null,
    overrideUnsafeObs = null,
  ) => {
    try {
      setIsSaving(true);
      // Basic validation
      if (!formData.date || !formData.startTime || !formData.duration) {
        toast.warn("Please select Observation Date, Start Time and Duration.");
        return false;
      }

      if (!formData.plantId || !formData.departmentId) {
        toast.warn("Please select Plant and Department.");
        return false;
      }

      // Settings Validation
      if (
        sboSettings.maxDuration &&
        parseInt(formData.duration) > parseInt(sboSettings.maxDuration)
      ) {
        toast.warn(
          `Duration cannot exceed the maximum allowed duration of ${sboSettings.maxDuration} minutes.`,
        );
        return false;
      }

      // Calculate observation time once
      const observationTime = new Date(
        `${formData.date}T${formData.startTime.length === 5
          ? formData.startTime + ":00"
          : formData.startTime
        }`,
      );
      const currentTime = new Date();

      // Check Submission Hours Limit
      if (sboSettings.submissionHours) {
        const diffHours = (currentTime - observationTime) / (1000 * 60 * 60);
        if (diffHours > parseInt(sboSettings.submissionHours)) {
          toast.warn(
            `Observation submission exceeds the allowed limit of ${sboSettings.submissionHours} hours from the observation time.`,
          );
          return false;
        }
      }

      // Check Non-Office Hours
      if (sboSettings.allowNonOfficeHours === false) {
        const obsHour = observationTime.getHours();
        // Office hours: 9 AM (inclusive) to 6 PM (exclusive, i.e., up to 17:59)
        const officeStart = 9;
        const officeEnd = 18;

        if (obsHour < officeStart || obsHour >= officeEnd) {
          toast.warn(
            "Observation submission is not allowed during non-office hours (Standard Office Hours: 09:00 AM - 06:00 PM).",
          );
          return false;
        }
      }

      // Use overrides if provided, otherwise use state
      const safeObsToUse = overrideSafeObs || safeObservations;
      const unsafeObsToUse = overrideUnsafeObs || unsafeObservations;

      const payload = {
        roundId: roundId || null,
        observationNumber: formData.observationNumber || "",
        roundName: `SBO - ${formData.date}`,
        observationDate: formData.date ? new Date(formData.date) : new Date(),
        observationTime: formData.startTime
          ? formData.startTime.includes(":") &&
            formData.startTime.split(":").length === 2
            ? `${formData.startTime}:00`
            : formData.startTime
          : "09:00:00",
        observationDuration: parseInt(formData.duration) || 0,
        description: formData.description
          ? String(formData.description).substring(0, 199)
          : "",
        natureOfWork: Array.isArray(formData.natureOfWork)
          ? formData.natureOfWork
          : formData.natureOfWork
            ? [formData.natureOfWork]
            : [],
        plantId: parseInt(formData.plantId) || null,
        departmentId: parseInt(formData.departmentId) || null,
        area: formData.area || "General Area",
        // statusId: 1, // Removed: Let backend handle default status lookup
        companyEmployeeCount: parseInt(formData.noCompanyEmp) || 0,
        contractorEmployeeCount: parseInt(formData.noContractorEmp) || 0,
        companyEmployeeNames: formData.selectCompanyEmp || [],
        contractorEmployeeNames: formData.selectContractorEmp || [],
        scheduleId: scheduleId || null,
        observers: formData.observationTeam
          .map((userId) => parseInt(userId))
          .filter((id) => !isNaN(id))
          .map((id) => ({
            observerUserId: id,
            observerRole: "Member",
          })),
        observationItems: [
          ...safeObsToUse.map((obs) => ({
            itemId: obs.id > 1000000000 ? 0 : obs.id,
            observationType: obs.type || "Safe Act",
            categoryId: obs.categoryId,
            description: obs.details
              ? String(obs.details).trim() || "Safe Observation"
              : "Safe Observation",
            observerType:
              obs.contractorEmpObserved && obs.contractorEmpObserved.length > 0
                ? "Contractor Employee"
                : obs.companyEmpObserved && obs.companyEmpObserved.length > 0
                  ? "Company Employee"
                  : "Self",
            observerName: (obs.companyEmpObserved &&
              obs.companyEmpObserved.length > 0 &&
              obs.contractorEmpObserved &&
              obs.contractorEmpObserved.length > 0
              ? `COMP:${obs.companyEmpObserved}|CONT:${obs.contractorEmpObserved}`
              : (obs.contractorEmpObserved &&
                obs.contractorEmpObserved.length > 0
                ? obs.contractorEmpObserved
                : "") ||
              (obs.companyEmpObserved && obs.companyEmpObserved.length > 0
                ? obs.companyEmpObserved
                : "") ||
              "Self"
            ).substring(0, 499),
            contractorName: obs.contractorEmpObserved || "",
            subCategoryId: parseInt(obs.subCategory) || null,
            actionDescription: "",
            itemOrder: 1,
            itemStatus: "Closed",
            companyEmployeeCount: parseInt(obs.ownEmpCount) || 0,
            contractorEmployeeCount: parseInt(obs.contractorEmpCount) || 0,
            contractorAgencyName: obs.contractorAgencyName || "",
            attachments: obs.attachments || [],
          })),
          ...unsafeObsToUse.map((obs) => ({
            itemId: obs.id > 1000000000 ? 0 : obs.id,
            observationType: obs.type || "Unsafe Act",
            categoryId: obs.categoryId,
            description: obs.details
              ? String(obs.details).trim() || "Unsafe Observation"
              : "Unsafe Observation",
            riskLevel: obs.severity || "Minor",
            itemStatus: obs.isClosed ? "Closed" : "Open",
            actionDescription: obs.actionTaken || "",
            observerType:
              obs.contractorEmpObserved && obs.contractorEmpObserved.length > 0
                ? "Contractor Employee"
                : obs.companyEmpObserved && obs.companyEmpObserved.length > 0
                  ? "Company Employee"
                  : "Self",
            observerName: (obs.companyEmpObserved &&
              obs.companyEmpObserved.length > 0 &&
              obs.contractorEmpObserved &&
              obs.contractorEmpObserved.length > 0
              ? `COMP:${obs.companyEmpObserved}|CONT:${obs.contractorEmpObserved}`
              : (obs.contractorEmpObserved &&
                obs.contractorEmpObserved.length > 0
                ? obs.contractorEmpObserved
                : "") ||
              (obs.companyEmpObserved && obs.companyEmpObserved.length > 0
                ? obs.companyEmpObserved
                : "") ||
              "Self"
            ).substring(0, 499),
            contractorName: obs.contractorEmpObserved || "",
            subCategoryId: parseInt(obs.subCategory) || null,
            itemOrder: 2,
            companyEmployeeCount: parseInt(obs.ownEmpCount) || 0,
            contractorEmployeeCount: parseInt(obs.contractorEmpCount) || 0,
            contractorAgencyName: obs.contractorAgencyName || "",
            attachments: obs.attachments || [],
            linkedCapaId: obs.linkedCapaId,
          })),
        ],
        // You might need to set StatusId here if you fetched statuses
        // For now, let's rely on backend default or implicit logic
        // If draft, maybe append to description or use a specific field if available
      };

      if (isDraft) {
        // If we have a way to set status to draft, do it here.
        // Assuming backend handles it or we need to pass StatusId.
        // For now, alerting user that it's being saved (logic same as save).
        payload.description = "[DRAFT] " + (payload.description || "");
      }

      console.log("Saving payload:", payload);

      // Clear CAPA cache so the CAPA module refreshes the new title
      sessionStorage.removeItem("capa_records_data");

      if (roundId) {
        // Update existing round
        await safetyObservationService.updateSafetyObservationRound(
          roundId,
          payload,
        );
        toast.success(
          `Safety Observation Round ${isDraft ? "Draft " : ""}updated successfully!`,
        );
      } else {
        // Create new round
        await safetyObservationService.createSafetyObservationRound(payload);
        toast.success(
          `Safety Observation Round ${isDraft ? "Draft " : ""}saved successfully!`,
        );
      }
      onClose(); // This closes Submitsbo
      return true;
    } catch (err) {
      console.error("Error saving observation round:", err);
      toast.error(
        "Failed to save observation round. Please check console for details.",
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div
        className={`side-drawer-backdrop ${isOpen ? "open" : ""}`}
        style={{ display: capaId ? "none" : "block" }}
      ></div>
      <div
        className={`side-drawer ${isOpen ? "open" : ""}`}
        style={{ display: capaId ? "none" : undefined }}
      >
        <div className="drawer-header">
          <h3>
            {roundId && formData.observationNumber
              ? formData.observationNumber
              : roundId
                ? "Edit Observation"
                : "Submit Safety Observation"}
          </h3>
          <div className="header-actions">
            <div className="action-btns">
              {!roundId && (
                <button
                  className="btn btnsavedraft"
                  onClick={handleSaveDraft}
                  disabled={isSaving}

                >
                  {isSaving ? "Saving..." : "Save Draft"}
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={() => handleSave(false)}
                disabled={isSaving}
                style={{ padding: "10px 24px", height: "44px" }}
              >
                {isSaving ? "Submitting..." : roundId ? "Update" : "Submit Report"}
              </button>
            </div>
            <button className="drawer-close-btn" onClick={onClose} style={{ width: "40px", height: "40px", borderRadius: "12px" }}>
              <FiX />
            </button>
          </div>
        </div>

        <div className="drawer-content">
          {/* Observation Details */}
          <div className="form-section">
            <h3>Observation Details</h3>
            <div className="form-row">
              <div className="form-col">
                <label className="required-label">Plant</label>
                <select
                  name="plantId"
                  value={formData.plantId}
                  onChange={handleInputChange}
                >
                  <option value="" disabled>
                    Please Select Plant
                  </option>
                  {plants.map((p) => (
                    <option key={p.plantId || p.id} value={p.plantId || p.id}>
                      {p.plantName || p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-col">
                <label className="required-label">Department</label>
                <select
                  name="departmentId"
                  value={formData.departmentId}
                  onChange={handleInputChange}
                  disabled={!formData.plantId}
                >
                  <option value="" disabled>
                    Please Select Department
                  </option>
                  {departments.map((d) => (
                    <option
                      key={d.departmentId || d.id}
                      value={d.departmentId || d.id}
                    >
                      {d.departmentName || d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-col">
                <label className="required-label">Observation Date :</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-col">
                <label className="required-label">Start Time :</label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-col">
                <label className="required-label">Duration :</label>
                <input
                  type="number"
                  name="duration"
                  min="0"
                  placeholder="in minutes"
                  value={formData.duration}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-col">
                <label>Nature of Work :</label>
                <CustomMultiSelect
                  label="Nature of Work"
                  options={natureOptions}
                  selectedValues={formData.natureOfWork}
                  onChange={handleMultiSelectChange}
                  name="natureOfWork"
                />
              </div>
            </div>

            <div className="form-row full-width">
              <div className="form-col">
                <label>Work/Job Description :</label>
                <textarea
                  name="description"
                  placeholder="E.g., Creating a user login flow with email validation..."
                  value={formData.description}
                  onChange={handleInputChange}
                  style={{
                    minHeight: "80px",
                    resize: "vertical",
                    paddingTop: "10px",
                    width: "100%",
                    border: "1px solid #cbd5e1",
                    borderRadius: "12px",
                    padding: "10px 16px",
                    fontSize: "14px",
                    color: "#0f172a",
                    outline: "none",
                  }}
                />
              </div>
            </div>
            {sboSettings.teamObservations !== false && (
              <div className="form-row">
                <div className="form-col">
                  <label>Observation Team :</label>
                  <CustomMultiSelect
                    label="Observation Team"
                    options={users.map((u) => ({
                      value: String(u.userId || u.id),
                      label: u.fullName || u.username,
                    }))}
                    selectedValues={formData.observationTeam}
                    onChange={handleMultiSelectChange}
                    name="observationTeam"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Observed At */}
          <div className="form-section">
            <h3 style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              Observed at
              <button
                type="button"
                className="btn-icon-only"
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: "#e0e7ff",
                  color: "#4f46e5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/home/settings/site-layout")}
                title="Configure Site Layout"
              >
                <FiPlus size={16} />
              </button>
            </h3>
            <div className="form-row" style={{ width: "100%" }}>
              {!formData.plantId ? (
                <div
                  style={{
                    width: "313%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "30px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px dashed #e2e8f0; border-color: var(--border-primary)",
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "50%",
                      backgroundColor: "#e0f2fe",
                      color: "#0284c7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "12px",
                    }}
                  >
                    <FiInfo size={24} />
                  </div>
                  <span
                    style={{
                      color: "#475569",
                      fontSize: "1rem",
                      fontWeight: "500",
                    }}
                  >
                    Please select a Plant to configure the Facility
                  </span>
                  <span
                    style={{
                      color: "#94a3b8",
                      fontSize: "0.875rem",
                      marginTop: "6px",
                    }}
                  >
                    Facility options will appear here based on the selected
                    plant's layout
                  </span>
                </div>
              ) : siteLayout && siteLayout.level1 ? (
                [1, 2, 3, 4, 5, 6]
                  .filter(
                    (level) =>
                      !sboSettings.facilityLevel ||
                      level <= parseInt(sboSettings.facilityLevel),
                  )
                  .map((level) => {
                    const levelName = siteLayout[`level${level}`];
                    // Only render if level name exists
                    if (!levelName) return null;

                    const allLevelItems = layoutItems[level] || [];
                    let items = allLevelItems;

                    // Filter items for child levels based on parent selection
                    if (level > 1) {
                      const parentId = selectedLayout[level - 1];
                      if (!parentId) {
                        items = [];
                      } else {
                        items = allLevelItems.filter(
                          (item) => String(item.parentId) === String(parentId),
                        );
                      }
                    }

                    // Disabled if previous level is not selected (for levels > 1)
                    const isDisabled = level > 1 && !selectedLayout[level - 1];

                    return (
                      <div
                        className="form-col"
                        key={level}
                        style={{ minWidth: "200px" }}
                      >
                        <label>{levelName}</label>
                        <select
                          value={selectedLayout[level] || ""}
                          onChange={(e) =>
                            handleLayoutChange(level, e.target.value)
                          }
                          disabled={isDisabled}
                        >
                          <option value="">Select {levelName}</option>
                          {items.map((item) => (
                            <option
                              key={item.id || item.itemId}
                              value={item.id || item.itemId}
                            >
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })
              ) : null}
            </div>
          </div>

          <div className="form-section">
            <h3>People Contacted</h3>
            <div className="form-row">
              <div className="form-col">
                <label>No. of Company Employee :</label>
                <input
                  type="number"
                  name="noCompanyEmp"
                  value={formData.noCompanyEmp}
                  placeholder="Count"
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-col">
                <label>No. of Contractor Employee :</label>
                <input
                  type="number"
                  name="noContractorEmp"
                  value={formData.noContractorEmp}
                  placeholder="Count"
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-col">
                {sboSettings.nameEmployees !== false && (
                  <>
                    <label>Company Employee Names :</label>
                    <CustomMultiSelect
                      name="selectCompanyEmp"
                      label="Select Employee Names"
                      options={(users || []).map((u) => ({
                        label: u.fullName || u.userName || u.username,
                        value: u.fullName || u.userName || u.username,
                      }))}
                      selectedValues={formData.selectCompanyEmp || []}
                      onChange={handleMultiSelectChange}
                    />
                  </>
                )}
              </div>
            </div>
            <div className="form-row">
              <div className="form-col">
                {sboSettings.nameEmployees !== false && (
                  <>
                    <label>Contractor Employee Names :</label>
                    <CustomMultiSelect
                      label="Select Contractor Names"
                      options={[
                        ...new Set(
                          agencies.flatMap((a) => a.contractorNames || []),
                        ),
                      ].map((name) => ({ label: name, value: name }))}
                      selectedValues={formData.selectContractorEmp || []}
                      onChange={handleMultiSelectChange}
                      name="selectContractorEmp"
                    />
                  </>
                )}
              </div>
              <div className="form-col"></div>
              <div className="form-col"></div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header-modern">
              <h3>Safe Observations</h3>
              <div className="section-header-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => openModal("Add Safe Observation", "Safe")}
                >
                  <FiPlus /> ADD
                </button>
              </div>
            </div>
            <table className="observation-table">
              <thead>
                <tr>
                  <th>Observation Details</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {safeObservations.map((obs) => (
                  <React.Fragment key={obs.id}>
                    <tr>
                      <td>
                        <span
                          className="expand-row"
                          onClick={() => toggleRow(`safe-${obs.id}`)}
                        >
                          {expandedRows[`safe-${obs.id}`] ? (
                            <FiChevronDown />
                          ) : (
                            <FiPlus />
                          )}
                        </span>
                        {obs.details}
                      </td>
                      <td>
                        {categories.find(
                          (c) =>
                            String(c.categoryId || c.CategoryId) ===
                            String(obs.categoryId),
                        )?.categoryName ||
                          categories.find(
                            (c) =>
                              String(c.categoryId || c.CategoryId) ===
                              String(obs.categoryId),
                          )?.CategoryName ||
                          obs.category ||
                          "Unknown"}
                      </td>
                      <td>{obs.type}</td>
                      <td className="text-right">
                        <div className="action-btns">
                          <button
                            type="button"
                            className="btn-icon-edit"
                            onClick={() =>
                              openModal("Edit Safe Observation", "Safe", obs)
                            }
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            type="button"
                            className="btn-icon-delete"
                            onClick={() => deleteObservation(obs.id, "Safe")}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows[`safe-${obs.id}`] && (
                      <tr className="expanded-row-content">
                        <td colSpan="4">
                          <div className="expanded-details">
                            <p>
                              <strong>Sub Category:</strong> {obs.subCategory}
                            </p>
                            <p>
                              <strong>Company Employee:</strong>{" "}
                              {obs.companyEmpObserved}
                            </p>
                            <p>
                              <strong>Contractor Employee:</strong>{" "}
                              {obs.contractorEmpObserved}
                            </p>
                            {obs.attachments?.length > 0 && (
                              <div className="attachment-details">
                                <strong>Attachments:</strong>
                                <div className="attachment-links">
                                  {obs.attachments.map((at, idx) => (
                                    <a
                                      key={idx}
                                      href={at.fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="attachment-link"
                                    >
                                      <FiUpload /> {at.fileName}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="form-section">
            <div className="section-header-modern">
              <h3>Unsafe Observations</h3>
              <div className="section-header-actions">
                <button
                  type="button"
                  className="btn btn-secondary mr-2"
                  onClick={() => setIsAIFeatureOpen(true)}
                >
                  <HiOutlineCpuChip /> AI Scan
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => openModal("Unsafe Observation", "Unsafe")}
                >
                  <FiPlus /> ADD
                </button>
              </div>
            </div>
            <table className="observation-table">
              <thead>
                <tr>
                  <th>Observation Details</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Severity</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {unsafeObservations.map((obs) => (
                  <React.Fragment key={obs.id}>
                    <tr>
                      <td>
                        <span
                          className="expand-row"
                          onClick={() => toggleRow(`unsafe-${obs.id}`)}
                        >
                          {expandedRows[`unsafe-${obs.id}`] ? (
                            <FiChevronDown />
                          ) : (
                            <FiPlus />
                          )}
                        </span>
                        {obs.details}
                      </td>
                      <td>
                        {categories.find(
                          (c) =>
                            String(c.categoryId || c.CategoryId) ===
                            String(obs.categoryId),
                        )?.categoryName ||
                          categories.find(
                            (c) =>
                              String(c.categoryId || c.CategoryId) ===
                              String(obs.categoryId),
                          )?.CategoryName ||
                          obs.category ||
                          "Unknown"}
                      </td>
                      <td>{obs.type}</td>
                      <td>{obs.severity}</td>
                      <td className="text-right">
                        <div className="action-btns">
                          <button
                            type="button"
                            className="btn-icon-edit"
                            onClick={() =>
                              openModal(
                                "Edit Unsafe Observation",
                                "Unsafe",
                                obs,
                              )
                            }
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            type="button"
                            className="btn-icon-delete"
                            onClick={() => deleteObservation(obs.id, "Unsafe")}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedRows[`unsafe-${obs.id}`] && (
                      <tr className="expanded-row-content">
                        <td colSpan="5">
                          <div className="expanded-details">
                            <p>
                              <strong>Sub Category:</strong> {obs.subCategory}
                            </p>
                            <p>
                              <strong>Company Employee:</strong>{" "}
                              {obs.companyEmpObserved || "N/A"}
                            </p>
                            <p>
                              <strong>Contractor Employee:</strong>{" "}
                              {obs.contractorEmpObserved || "N/A"}
                            </p>
                            <p>
                              <strong>Action Taken/Required:</strong>{" "}
                              {obs.actionTaken}
                            </p>
                            <p>
                              <strong>Status:</strong>{" "}
                              {obs.isClosed ? "Closed" : "Open"}
                            </p>
                            {obs.attachments?.length > 0 && (
                              <div className="attachment-details">
                                <strong>Attachments:</strong>
                                <div className="attachment-links">
                                  {obs.attachments.map((at, idx) => (
                                    <a
                                      key={idx}
                                      href={at.fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="attachment-link"
                                    >
                                      <FiUpload /> {at.fileName}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Observation Modal - Rendered outside the scrollable area but inside the dashboard theme */}
      <ObservationModal
        key={modalConfig.isOpen ? `${modalConfig.type}-open` : "closed"}
        isOpen={modalConfig.isOpen}
        onClose={() => {
          setModalConfig({ ...modalConfig, isOpen: false, initialFiles: [] }); // Reset initialFiles on close
          if (capaId) {
            onClose();
          }
        }}
        initialFiles={modalConfig.initialFiles} // Pass initialFiles prop
        onSave={handleObservationSave}
        title={modalConfig.title}
        type={modalConfig.type}
        categories={categories}
        initialData={editingObs}
        users={users}
        agencies={agencies}
        sboSettings={sboSettings}
        maxOwnEmp={formData.noCompanyEmp}
        maxContractorEmp={formData.noContractorEmp}
      />

      {/* AI Scanning Modal */}
      <AIObservationFeature
        isOpen={isAIFeatureOpen}
        onClose={() => setIsAIFeatureOpen(false)}
        onScanComplete={(result, files) => {
          setIsAIFeatureOpen(false);
          handleAiScanComplete(result, files);
        }}
      />
    </>
  );
};

const AIObservationFeature = ({ isOpen, onClose, onScanComplete }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStep, setScanStep] = useState("");
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const resetState = () => {
    setSelectedFiles([]);
    if (previewUrls.length > 0) {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    }
    setPreviewUrls([]);
    setIsScanning(false);
    setScanComplete(false);
    setScanProgress(0);
    setScanStep("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const processImages = (files) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (validFiles.length === 0) {
      toast.error("Please upload image files only");
      return;
    }

    setSelectedFiles(validFiles);

    // Create preview URLs
    const urls = validFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);

    startScan(validFiles);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processImages(e.dataTransfer.files);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processImages(e.target.files);
    }
  };

  const handleContainerClick = () => {
    if (selectedFiles.length === 0) {
      fileInputRef.current.click();
    }
  };

  const startScan = (files) => {
    setIsScanning(true);
    setScanComplete(false);
    setScanProgress(0);

    const steps = [
      "Initializing Safety Vision Engine...",
      "Analyzing Behavioral Patterns...",
      "Checking PPE Compliance...",
      "Classifying Observation Type...",
      "Finalizing Safety Insights...",
    ];

    let currentStep = 0;
    setScanStep(steps[currentStep]);

    const stepInterval = setInterval(() => {
      currentStep++;
      if (currentStep < steps.length) {
        setScanStep(steps[currentStep]);
      }
    }, 400);

    // Run actual API call asynchronously with the FIRST image
    let apiData = null;
    let apiError = false;
    let isApiDone = false;
    const fileToScan = files[0];

    aiService
      .analyzeObservationImage(fileToScan)
      .then((res) => {
        apiData = res;
        isApiDone = true;
      })
      .catch((err) => {
        console.error("AI SBO Analysis Failed", err);
        apiError = true;
        isApiDone = true;
      });

    let progress = 0;
    const progressInterval = setInterval(() => {
      // Don't reach 100% until API is done
      if (isApiDone || progress < 85) {
        progress += Math.floor(Math.random() * 8) + 4;
      }

      if (progress >= 100) progress = 100;
      setScanProgress(progress);

      // Once visually 100% AND API is done, proceed
      if (progress >= 100 && isApiDone) {
        clearInterval(progressInterval);
        clearInterval(stepInterval);
        setIsScanning(false);
        setScanComplete(true);

        const finalResult =
          !apiError && apiData
            ? apiData
            : {
              description:
                "Image scanned. Please manually review the findings.",
              suggested_action: "Examine behaviors and provide feedback.",
              observation_type: "Unsafe Act",
              risk_level: "Moderate",
            };

        setTimeout(() => {
          if (onScanComplete) {
            onScanComplete(finalResult, files);
          }
        }, 500); // Small delay to read "Analysis Complete"
      }
    }, 100);
  };

  return (
    <SharedModal
      isOpen={isOpen}
      onClose={handleClose}
      title="AI Safety Interface"
      customWidth="780px"
      footer={
        <div className="ai-modal-footer" style={{ borderTop: 'none', padding: 0 }}>
          {(previewUrls.length > 0 && !isScanning && !scanComplete && (
            <button className="btn btn-secondary" onClick={resetState}>
              Upload Different Image
            </button>
          )) || (
              <button className="btn btn-secondary" onClick={handleClose}>
                Cancel
              </button>
            )}
        </div>
      }
    >
      <div className="shared-modal-body">
          {previewUrls.length === 0 ? (
            <div
              className={`ai-uploader-container pulse-border`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleContainerClick}
            >
              <div className="ai-upload-icon-wrapper">
                <HiOutlineArrowUpTray className="ai-uploader-icon" />
              </div>
              <div className="ai-uploader-text">
                <h3>Drag & Drop Observation Images</h3>
                <p>Upload photos of workplace activities for AI analysis</p>
              </div>
              <input
                type="file"
                multiple
                className="ai-file-input"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </div>
          ) : (
            <div className="ai-scanner-view">
              <div className="ai-image-preview-container" style={{ position: 'relative' }}>
                <div className="ai-preview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                  {previewUrls.slice(0, 4).map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`SBO Preview ${idx + 1}`}
                      className={`ai-preview-image ${previewUrls.length > 1 ? "multiple" : ""}`}
                      style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                  ))}
                  {previewUrls.length > 4 && (
                    <div className="ai-preview-more">
                      +{previewUrls.length - 4} More
                    </div>
                  )}
                </div>

                {isScanning && (
                  <div className="ai-scan-layer" style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.7)', borderRadius: '8px', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifySelf: 'center', padding: '40px' }}>
                    <div className="ai-scanning-overlay professional" style={{ color: 'white', textAlign: 'center' }}>
                      <div className="ai-progress-circle" style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '10px' }}>
                        <span>{scanProgress}%</span>
                      </div>
                      <span className="ai-scan-step-text" style={{ fontSize: '1rem', opacity: 0.9 }}>{scanStep}</span>
                      <div className="ai-progress-bar-container" style={{ width: '240px', height: '6px', background: 'rgba(255,255,255,0.2)', borderRadius: '3px', marginTop: '15px', overflow: 'hidden' }}>
                        <div
                          className="ai-progress-bar-fill"
                          style={{ width: `${scanProgress}%`, height: '100%', background: '#6366f1' }}
                        ></div>
                      </div>
                    </div>
                  </div>
                )}

                {scanComplete && (
                  <div className="ai-scan-layer" style={{ position: 'absolute', inset: 0, background: 'rgba(16, 185, 129, 0.9)', borderRadius: '8px', zIndex: 11, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
                    <div className="ai-scanning-overlay professional" style={{ color: 'white', textAlign: 'center' }}>
                      <HiOutlineCheckCircle size={56} style={{ marginBottom: '15px' }} />
                      <span
                        style={{
                          fontSize: "1.25rem",
                          fontWeight: "800",
                          display: 'block'
                        }}
                      >
                        Analysis Complete
                      </span>
                      <p
                        style={{
                          opacity: 0.9,
                          fontSize: "0.9rem",
                          marginTop: '5px'
                        }}
                      >
                        Compiling Safety Observation...
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>
    </SharedModal>
  );
};

export default Submitsbo;
